<script setup>
import { ref, computed, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import CampaignBatchesAPI from 'dashboard/api/crm/campaignBatches';

const emit = defineEmits(['close', 'success']);
const store = useStore();

// ── form state ──────────────────────────────────────────────────────────────
const step = ref(1); // 1 = info, 2 = destinatários, 3 = revisar
const createdBatch = ref(null);

const form = ref({
  name: '',
  subject: '',
  html_content: '',
  scheduled_at: '',
});

const segmentTab = ref('filters'); // 'filters' | 'manual'

// ── Segment Filters ──────────────────────────────────────────────────────────
const FILTER_OPERATORS = {
  email_address: [
    { value: 'equal_to', label: 'é igual a' },
    { value: 'not_equal_to', label: 'não é igual a' },
    { value: 'contains', label: 'contém' },
    { value: 'present', label: 'está preenchido' },
  ],
  name: [
    { value: 'contains', label: 'contém' },
    { value: 'equal_to', label: 'é igual a' },
  ],
  labels: [
    { value: 'equal_to', label: 'tem etiqueta' },
    { value: 'not_equal_to', label: 'não tem etiqueta' },
  ],
  city: [
    { value: 'equal_to', label: 'é' },
    { value: 'contains', label: 'contém' },
  ],
  country_code: [
    { value: 'equal_to', label: 'é' },
  ],
  phone_number: [
    { value: 'contains', label: 'contém' },
    { value: 'present', label: 'está preenchido' },
  ],
};

const FILTER_ATTRIBUTES = [
  { value: 'email_address', label: '📧 Email' },
  { value: 'name', label: '👤 Nome' },
  { value: 'phone_number', label: '📱 Telefone' },
  { value: 'labels', label: '🏷️ Etiqueta' },
  { value: 'city', label: '🏙️ Cidade' },
  { value: 'country_code', label: '🌍 País' },
];

const DEFAULT_CONDITION = () => ({
  attribute_key: 'email_address',
  filter_operator: 'present',
  values: [],
  query_operator: 'and',
});

const conditions = ref([DEFAULT_CONDITION()]);

const accountLabels = computed(() => {
  const labels = store.getters['labels/getLabels'] || [];
  return labels.map(l => l.title);
});

const operatorsFor = attr => FILTER_OPERATORS[attr] || FILTER_OPERATORS.name;
const needsValue = op => !['present', 'not_present'].includes(op);

const addCondition = () => conditions.value.push(DEFAULT_CONDITION());
const removeCondition = i => {
  if (conditions.value.length > 1) conditions.value.splice(i, 1);
};

watch(
  () => conditions.value.map(c => c.attribute_key),
  (keys, oldKeys) => {
    keys.forEach((key, i) => {
      if (key !== oldKeys[i]) {
        const ops = operatorsFor(key);
        conditions.value[i].filter_operator = ops[0]?.value || 'equal_to';
        conditions.value[i].values = [];
      }
    });
  }
);

// ── Manual contacts ──────────────────────────────────────────────────────────
const manualEmails = ref('');

// ── UI state ─────────────────────────────────────────────────────────────────
const isCreating = ref(false);
const isPopulating = ref(false);
const populateResult = ref(null);
const errors = ref({});

const currentDateTime = computed(() => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
});

// ── Step 1: create batch ──────────────────────────────────────────────────────
const validateStep1 = () => {
  errors.value = {};
  if (!form.value.name.trim()) errors.value.name = 'Nome é obrigatório.';
  if (!form.value.subject.trim()) errors.value.subject = 'Assunto é obrigatório.';
  if (!form.value.html_content.trim()) errors.value.html_content = 'Conteúdo é obrigatório.';
  return Object.keys(errors.value).length === 0;
};

const createBatch = async () => {
  if (!validateStep1()) return;
  isCreating.value = true;
  try {
    const payload = {
      campaign_batch: {
        name: form.value.name,
        subject: form.value.subject,
        html_content: form.value.html_content,
        batch_type: 'email_marketing',
        source_type: 'manual',
        scheduled_at: form.value.scheduled_at || null,
      },
    };
    const { data } = await CampaignBatchesAPI.create(payload);
    createdBatch.value = data;
    step.value = 2;
  } catch (e) {
    useAlert(e.response?.data?.error || 'Erro ao criar campanha.');
  } finally {
    isCreating.value = false;
  }
};

// ── Step 2: populate recipients ───────────────────────────────────────────────
const buildFilterPayload = () => {
  return conditions.value.map((c, i) => ({
    attribute_key: c.attribute_key,
    filter_operator: c.filter_operator,
    values: Array.isArray(c.values) ? c.values : [c.values],
    query_operator: i === conditions.value.length - 1 ? null : c.query_operator,
  }));
};

const populateFromSegment = async () => {
  isPopulating.value = true;
  populateResult.value = null;
  try {
    const filters = buildFilterPayload();
    const { data } = await CampaignBatchesAPI.populateFromSegment(createdBatch.value.id, filters);
    populateResult.value = data;
    if (data.added_count > 0) step.value = 3;
    else useAlert('Nenhum contato com email encontrado para estes filtros.');
  } catch (e) {
    useAlert(e.response?.data?.error || 'Erro ao aplicar segmento.');
  } finally {
    isPopulating.value = false;
  }
};

const populateManual = async () => {
  const emails = manualEmails.value
    .split(/[\n,;]+/)
    .map(e => e.trim())
    .filter(e => e.includes('@'));

  if (!emails.length) { useAlert('Informe ao menos um email válido.'); return; }

  // Look up contacts by email via filter
  isPopulating.value = true;
  populateResult.value = null;
  try {
    const filters = emails.map((email, i) => ({
      attribute_key: 'email_address',
      filter_operator: 'equal_to',
      values: [email],
      query_operator: i === emails.length - 1 ? null : 'or',
    }));
    const { data } = await CampaignBatchesAPI.populateFromSegment(createdBatch.value.id, filters);
    populateResult.value = data;
    if (data.added_count > 0) step.value = 3;
    else useAlert('Nenhum contato encontrado para os emails informados.');
  } catch (e) {
    useAlert(e.response?.data?.error || 'Erro ao adicionar contatos.');
  } finally {
    isPopulating.value = false;
  }
};

const finalize = () => {
  emit('success');
};
</script>

<template>
  <!-- Backdrop -->
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
    <div class="w-full max-w-2xl bg-white dark:bg-n-solid-2 rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">

      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-n-weak flex-shrink-0">
        <div class="flex items-center gap-3">
          <div class="size-9 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
            <span class="i-lucide-mail text-blue-500 size-5" />
          </div>
          <div>
            <h2 class="text-sm font-bold text-n-slate-12">Nova Campanha de E-mail</h2>
            <p class="text-xs text-n-slate-10">
              <span :class="step >= 1 ? 'text-blue-500 font-semibold' : ''">Configurar</span>
              → <span :class="step >= 2 ? 'text-blue-500 font-semibold' : ''">Destinatários</span>
              → <span :class="step >= 3 ? 'text-blue-500 font-semibold' : ''">Pronto</span>
            </p>
          </div>
        </div>
        <button class="p-1.5 rounded-lg hover:bg-n-alpha-2 transition-colors cursor-pointer" @click="emit('close')">
          <span class="i-lucide-x size-4 text-n-slate-10" />
        </button>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-auto px-6 py-5">

        <!-- ── Step 1: Info ── -->
        <div v-if="step === 1" class="flex flex-col gap-4">
          <div>
            <label class="text-xs font-semibold text-n-slate-11 mb-1 block">Nome da campanha *</label>
            <input v-model="form.name" class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400" placeholder="Ex: Promoção Junho 2026" />
            <p v-if="errors.name" class="text-xs text-red-500 mt-1">{{ errors.name }}</p>
          </div>

          <div>
            <label class="text-xs font-semibold text-n-slate-11 mb-1 block">Assunto do e-mail *</label>
            <input v-model="form.subject" class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400" placeholder="Ex: Olá {{contact.first_name}}, temos uma novidade!" />
            <p v-if="errors.subject" class="text-xs text-red-500 mt-1">{{ errors.subject }}</p>
          </div>

          <div>
            <label class="text-xs font-semibold text-n-slate-11 mb-1 block">Corpo do e-mail (HTML) *</label>
            <textarea v-model="form.html_content" rows="7"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 font-mono"
              placeholder="<p>Olá {{contact.first_name}},</p><p>Sua mensagem aqui...</p>" />
            <p v-if="errors.html_content" class="text-xs text-red-500 mt-1">{{ errors.html_content }}</p>
            <p v-pre class="text-[10px] text-n-slate-9 mt-1">Suporta variáveis Liquid: <code class="bg-n-alpha-1 px-1 rounded">{{contact.name}}</code>, <code class="bg-n-alpha-1 px-1 rounded">{{contact.email}}</code></p>
          </div>

          <div>
            <label class="text-xs font-semibold text-n-slate-11 mb-1 block">Agendamento (opcional)</label>
            <input v-model="form.scheduled_at" type="datetime-local" :min="currentDateTime"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400" />
            <p class="text-[10px] text-n-slate-9 mt-1">Deixe em branco para disparar manualmente após configurar os destinatários.</p>
          </div>
        </div>

        <!-- ── Step 2: Destinatários ── -->
        <div v-else-if="step === 2" class="flex flex-col gap-4">
          <!-- Tabs -->
          <div class="flex gap-1 p-1 bg-n-alpha-1 rounded-lg">
            <button
              class="flex-1 py-1.5 text-xs font-semibold rounded-md transition-colors cursor-pointer"
              :class="segmentTab === 'filters' ? 'bg-white dark:bg-n-solid-3 text-n-slate-12 shadow-sm' : 'text-n-slate-10'"
              @click="segmentTab = 'filters'"
            >
              🔍 Filtros de segmento
            </button>
            <button
              class="flex-1 py-1.5 text-xs font-semibold rounded-md transition-colors cursor-pointer"
              :class="segmentTab === 'manual' ? 'bg-white dark:bg-n-solid-3 text-n-slate-12 shadow-sm' : 'text-n-slate-10'"
              @click="segmentTab = 'manual'"
            >
              ✉️ Lista de e-mails
            </button>
          </div>

          <!-- Filters tab -->
          <div v-if="segmentTab === 'filters'" class="flex flex-col gap-3">
            <p class="text-xs text-n-slate-10">Defina critérios para selecionar contatos. Apenas contatos com e-mail preenchido serão adicionados.</p>

            <div v-for="(cond, i) in conditions" :key="i" class="flex items-start gap-2">
              <div v-if="i > 0" class="w-12 flex-shrink-0">
                <select v-model="cond.query_operator" class="w-full text-[10px] px-1 py-1.5 rounded border border-n-weak bg-n-background text-n-slate-11 focus:outline-none">
                  <option value="and">E</option>
                  <option value="or">OU</option>
                </select>
              </div>
              <div v-else class="w-12 flex-shrink-0 flex items-center justify-center h-8">
                <span class="text-[10px] text-n-slate-9 font-semibold">ONDE</span>
              </div>

              <select v-model="cond.attribute_key" class="text-xs px-2 py-1.5 rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-blue-400">
                <option v-for="attr in FILTER_ATTRIBUTES" :key="attr.value" :value="attr.value">{{ attr.label }}</option>
              </select>

              <select v-model="cond.filter_operator" class="text-xs px-2 py-1.5 rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-blue-400">
                <option v-for="op in operatorsFor(cond.attribute_key)" :key="op.value" :value="op.value">{{ op.label }}</option>
              </select>

              <div v-if="needsValue(cond.filter_operator)" class="flex-1">
                <!-- Label multiselect -->
                <div v-if="cond.attribute_key === 'labels'" class="flex flex-wrap gap-1">
                  <label v-for="lbl in accountLabels" :key="lbl"
                    class="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border cursor-pointer transition-colors"
                    :class="(cond.values || []).includes(lbl) ? 'border-blue-400 bg-blue-50 text-blue-600 dark:bg-blue-900/20' : 'border-n-weak text-n-slate-10 hover:border-blue-300'"
                  >
                    <input type="checkbox" class="hidden"
                      :checked="(cond.values || []).includes(lbl)"
                      @change="e => {
                        const arr = [...(cond.values || [])];
                        e.target.checked ? arr.push(lbl) : arr.splice(arr.indexOf(lbl), 1);
                        cond.values = arr;
                      }"
                    />
                    {{ lbl }}
                  </label>
                </div>
                <!-- Text input for other types -->
                <input v-else
                  :value="Array.isArray(cond.values) ? cond.values[0] : cond.values"
                  class="w-full text-xs px-2 py-1.5 rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-blue-400"
                  placeholder="Valor..."
                  @input="e => cond.values = [e.target.value]"
                />
              </div>
              <div v-else class="flex-1" />

              <button class="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors cursor-pointer" @click="removeCondition(i)">
                <span class="i-lucide-x size-3.5 text-red-400" />
              </button>
            </div>

            <button class="self-start text-xs text-blue-500 hover:text-blue-600 flex items-center gap-1 cursor-pointer" @click="addCondition">
              <span class="i-lucide-plus size-3" /> Adicionar condição
            </button>
          </div>

          <!-- Manual tab -->
          <div v-else class="flex flex-col gap-2">
            <p class="text-xs text-n-slate-10">Cole ou digite os e-mails dos destinatários, um por linha (ou separados por vírgula). Os contatos serão buscados pelo email.</p>
            <textarea v-model="manualEmails" rows="8"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 font-mono"
              placeholder="joao@empresa.com&#10;maria@cliente.com.br&#10;..." />
          </div>

          <!-- Result preview -->
          <div v-if="populateResult" class="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/15 border border-emerald-200 dark:border-emerald-800 text-xs">
            <p class="font-semibold text-emerald-700 dark:text-emerald-400">✅ Destinatários adicionados!</p>
            <p class="text-emerald-600 dark:text-emerald-300 mt-0.5">
              {{ populateResult.added_count }} adicionados · {{ populateResult.skipped_count }} duplicados ignorados
            </p>
          </div>
        </div>

        <!-- ── Step 3: Pronto ── -->
        <div v-else-if="step === 3" class="flex flex-col items-center py-8 gap-4">
          <div class="size-16 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
            <span class="i-lucide-check-circle text-emerald-500 size-8" />
          </div>
          <div class="text-center">
            <h3 class="text-base font-bold text-n-slate-12">Campanha criada com sucesso!</h3>
            <p class="text-sm text-n-slate-10 mt-1">
              <strong>{{ populateResult?.added_count || '?' }}</strong> destinatários adicionados.
              Acesse os detalhes para disparar.
            </p>
          </div>
          <div v-if="createdBatch?.scheduled_at" class="text-xs text-n-slate-9">
            Agendada para: {{ new Date(createdBatch.scheduled_at).toLocaleString('pt-BR') }}
          </div>
          <div v-else class="text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/15 px-3 py-1.5 rounded-lg">
            Sem agendamento — acesse os detalhes e clique em "Disparar" quando estiver pronto.
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-between px-6 py-4 border-t border-n-weak flex-shrink-0">
        <button
          v-if="step > 1 && step < 3"
          class="text-xs text-n-slate-10 hover:text-n-slate-12 transition-colors cursor-pointer"
          @click="step--"
        >
          ← Voltar
        </button>
        <div v-else />

        <div class="flex items-center gap-2">
          <button
            class="px-4 py-2 text-sm text-n-slate-11 hover:text-n-slate-12 transition-colors cursor-pointer"
            @click="emit('close')"
          >
            {{ step === 3 ? 'Fechar' : 'Cancelar' }}
          </button>

          <!-- Step 1: Criar -->
          <button v-if="step === 1"
            class="px-4 py-2 text-sm font-semibold text-white rounded-lg cursor-pointer transition-all disabled:opacity-50"
            style="background: linear-gradient(135deg, #3b82f6, #6366f1);"
            :disabled="isCreating"
            @click="createBatch"
          >
            <span v-if="isCreating" class="i-lucide-loader-2 size-4 animate-spin inline-block mr-1" />
            Continuar →
          </button>

          <!-- Step 2: Adicionar destinatários -->
          <button v-else-if="step === 2"
            class="px-4 py-2 text-sm font-semibold text-white rounded-lg cursor-pointer transition-all disabled:opacity-50"
            style="background: linear-gradient(135deg, #3b82f6, #6366f1);"
            :disabled="isPopulating"
            @click="segmentTab === 'filters' ? populateFromSegment() : populateManual()"
          >
            <span v-if="isPopulating" class="i-lucide-loader-2 size-4 animate-spin inline-block mr-1" />
            {{ segmentTab === 'filters' ? '🔍 Aplicar filtros' : '✉️ Adicionar contatos' }}
          </button>

          <!-- Step 3: Finalizar -->
          <button v-else-if="step === 3"
            class="px-4 py-2 text-sm font-semibold text-white rounded-lg cursor-pointer transition-all"
            style="background: linear-gradient(135deg, #22c55e, #16a34a);"
            @click="finalize"
          >
            ✅ Ver campanhas
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
